'use client';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useMyChannelBookmarks } from '../../hooks/useMyChannelBookmarks';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { BookmarkedMessageRow } from '../channel-info/BookmarkedMessageRow';
import { ChannelMessageListView } from '../channel-info/ChannelMessageListView';
import { ChannelPanelSheet } from '../channel-info/ChannelPanelSheet';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

/**
 * Bookmarks panel — the current user's saved messages in this
 * channel. Each row fetches the bookmarked message lazily so we
 * don't need to mirror message content onto the bookmark doc.
 *
 * "Jump to message" link sends the user to the thread URL of that
 * message — handy when the bookmark is deep in scroll. A
 * thread-less in-channel deep-link would be a nicer affordance but
 * requires the message list to support scroll-to-id, which slice 4
 * doesn't add yet.
 */
export function ChannelBookmarksSheet({ channel, open, onOpenChange }: Props) {
  const state = useMyChannelBookmarks(channel.id);
  const currentUserId = useCurrentUserId();

  return (
    <ChannelPanelSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Bookmarks"
    >
      <ChannelMessageListView
        items={state.bookmarks}
        isLoading={state.isLoading}
        error={state.error}
        errorLabel="Couldn’t load bookmarks."
        emptyLabel="You haven’t bookmarked any messages in this channel yet."
        keyOf={bookmark => bookmark.id}
        renderRow={bookmark => (
          <BookmarkedMessageRow
            channel={channel}
            message={bookmark.message}
            currentUserId={currentUserId}
          />
        )}
      />
    </ChannelPanelSheet>
  );
}
